import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDashboardApi } from '../useDashboardApi';
import { useNavigateToRoute } from 'utils/common';
import Cookies from 'js-cookie';
import { usePermission } from 'hooks/usePermission';

interface MetricCardProps {
  title: string;
  value: string | number;
  valueColor?: string;
  showViewDetails?: boolean;
  onClick?: () => void;
  summaryLoading?: boolean;
}

const CardContainer = styled.div`
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #dceaff;
  background-color: #edf4fd99;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64738e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
  white-space: nowrap;
`;

const Value = styled.div<{ color: string }>`
  font-size: 38px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${props => props.color};
`;

const ViewDetails = styled.div`
  font-size: 12px;
  color: #3b82f6;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #1d4ed8;
  }
`;

const DashboardContainer = styled.div`
  padding: 15px 0;
`;

const DashboardTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 15px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const LoadingCard = styled(CardContainer)`
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  valueColor = '#3B82F6',
  showViewDetails = true,
  onClick,
  summaryLoading,
}) => {
  if (summaryLoading) {
    return (
      <DashboardContainer>
        <GridContainer>
          {Array.from({ length: 6 }).map((_, index) => (
            <LoadingCard key={index} />
          ))}
        </GridContainer>
      </DashboardContainer>
    );
  }

  return (
    <CardContainer onClick={onClick}>
      <Title>{title}</Title>
      <Value color={valueColor}>{value}</Value>
      {showViewDetails && <ViewDetails>View Details →</ViewDetails>}
    </CardContainer>
  );
};

interface DashboardProps { }

const MetricsDashboard: React.FC<DashboardProps> = ({ }) => {
  const userType = Cookies.get('userType') ?? '';
  const redirect = useNavigateToRoute();
  const { totalSummary, getTotalSummary, summaryLoading } = useDashboardApi();
  useEffect(() => {
    getTotalSummary();
  }, []);
  const { hasViewPermission } = usePermission();

  const rawMetrics = [
    hasViewPermission('0200101001') && {
      title: 'ACTIVE TEAMS',
      value: totalSummary?.activeTeams || 0,
      valueColor: '#8B5CF6',
    },
    hasViewPermission('0200101001') && {
      title: 'INACTIVE TEAMS',
      value: totalSummary?.inactiveTeams || 0,
      valueColor: '#10B981',
    },
    hasViewPermission('0300101001') && {
      title: 'ACTIVE RIDERS',
      value: totalSummary?.activeRiders || 0,
      valueColor: '#3B82F6',
    },
    hasViewPermission('0300101001') && {
      title: 'INACTIVE RIDERS',
      value: totalSummary?.inactiveRiders || 0,
      valueColor: '#F59E0B',
    },
    hasViewPermission('0400101001') && {
      title: 'ACTIVE COACHES',
      value: totalSummary?.activeCoaches || 0,
      valueColor: '#6366F1',
    },
    hasViewPermission('0400101001') && {
      title: 'INACTIVE COACHES',
      value: totalSummary?.inactiveCoaches || 0,
      valueColor: '#EF4444',
    },
  ];
  const metrics = rawMetrics.filter(
    (item): item is { title: string; value: number; valueColor: string } =>
      Boolean(item),
  );
  const handleCardClick = (title: string) => {
    const baseRoute = userType === 'COACH' ? '/coach' : '/admin';
    switch (title) {
      case 'ACTIVE TEAMS':
        redirect(`${baseRoute}/teams/active`);
        break;
      case 'INACTIVE TEAMS':
        redirect(`${baseRoute}/teams/inactive`);
        break;
      case 'ACTIVE RIDERS':
        redirect(
          userType === 'COACH'
            ? '/coach/riders/active'
            : '/admin/riders/active',
        );
        break;
      case 'INACTIVE RIDERS':
        redirect(
          userType === 'COACH'
            ? '/coach/riders/active'
            : '/admin/riders/inactive',
        );
        break;
      case 'ACTIVE COACHES':
        redirect('/admin/coaches/active');
        break;
      case 'INACTIVE COACHES':
        redirect('/admin/coaches/inactive');
        break;
      default:
        console.warn('No action defined for this metric.');
    }
  };
  return (
    <DashboardContainer>
      <DashboardTitle>Statistics</DashboardTitle>
      <GridContainer>
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            title={metric.title}
            value={metric.value}
            valueColor={metric.valueColor}
            onClick={() => handleCardClick(metric.title)}
            summaryLoading={summaryLoading}
          />
        ))}
      </GridContainer>
    </DashboardContainer>
  );
};

export default MetricsDashboard;
